import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, RefreshCw, X } from 'lucide-react';
import BottomSheet from '../../../components/ui/BottomSheet';
import { Spinner } from '../../../components/ui';
import { cn, fmtDateTime } from '../../../lib/utils';
import { useManualFanoutEventDetail, useManualFanoutEvents } from '../../../api/hooks';

const STATUS_STYLE = {
  SUCCESS: 'bg-bull/10 border-bull/30 text-bull',
  PARTIAL_SUCCESS: 'bg-warn/10 border-warn/30 text-warn',
  FAILED: 'bg-bear/10 border-bear/30 text-bear',
  SKIPPED: 'bg-bg-hover border-bg-border text-txt-muted',
  QUEUED: 'bg-info/10 border-info/30 text-info',
  RUNNING: 'bg-info/10 border-info/30 text-info',
};

function FanoutStatusBadge({ status }) {
  const token = String(status || 'UNKNOWN').toUpperCase();
  return (
    <span className={cn('badge border whitespace-nowrap', STATUS_STYLE[token] || STATUS_STYLE.SKIPPED)}>
      {(token === 'QUEUED' || token === 'RUNNING') && <span className="mt-dot w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
      {token.replaceAll('_', ' ')}
    </span>
  );
}

function SymbolSummary({ source, base, resolved, compact = false }) {
  const sourceSymbol = base || source || '—';
  const resolvedSymbol = resolved || '—';
  const dynamic = sourceSymbol !== '—' && resolvedSymbol !== '—' && sourceSymbol !== resolvedSymbol;
  return (
    <div className={cn('font-mono', compact ? 'text-2xs' : 'text-xs')}>
      <div className="break-all"><span className="text-txt-label">Source:</span> <span className="text-txt-secondary">{sourceSymbol}</span></div>
      <div className="break-all"><span className="text-txt-label">{dynamic ? 'Option' : 'Executable'}:</span> <span className="text-txt-primary">{resolvedSymbol}</span></div>
    </div>
  );
}

function childCounts(event) {
  return `${event?.child_success_count || 0} / ${event?.child_failed_count || 0} / ${event?.child_skipped_count || 0} / ${event?.child_queued_count || 0}`;
}

function masterReference(event) {
  if (event?.master_broker_order_log_id != null) return `Order log #${event.master_broker_order_log_id}`;
  if (event?.master_signal_id != null) return `Signal #${event.master_signal_id}`;
  if (event?.master_signal_request_id != null) return `Signal request #${event.master_signal_request_id}`;
  return event?.source_client_request_id || '—';
}

function SummaryGrid({ event }) {
  if (!event) return null;
  const fields = [
    ['Event', `#${event.event_id}`],
    ['Created', fmtDateTime(event.created_at)],
    ['Action', event.event_action || '—'],
    ['Signal Action', event.signal_action || '—'],
    ['Status', event.status || '—'],
    ['Scope', String(event.fanout_scope || '—').replaceAll('_', ' ')],
    ['Master Strategy', event.master_strategy_id != null ? `#${event.master_strategy_id}` : '—'],
    ['Master Deployment', event.master_deployment_id != null ? `#${event.master_deployment_id}` : '—'],
    ['Master Reference', masterReference(event)],
    ['Children S/F/S/Q', childCounts(event)],
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2">
      {fields.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <p className="text-2xs font-mono uppercase tracking-widest text-txt-label">{label}</p>
          <p className="text-xs font-mono text-txt-primary break-all">{value}</p>
        </div>
      ))}
      <div className="sm:col-span-2">
        <SymbolSummary source={event.symbol} base={event.base_symbol} resolved={event.resolved_symbol} />
      </div>
    </div>
  );
}

function ChildCard({ child }) {
  return (
    <div className="rounded-md border border-bg-border bg-bg-surface/40 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-mono text-txt-primary">
          User #{child.child_user_id} · Deployment #{child.child_deployment_id}
        </div>
        <FanoutStatusBadge status={child.status} />
      </div>
      <SymbolSummary source={child.symbol} resolved={child.resolved_symbol} compact />
      <div className="grid grid-cols-2 gap-2 text-2xs font-mono">
        <span><span className="text-txt-label">Action:</span> {[child.event_action, child.signal_action].filter(Boolean).join(' · ') || '—'}</span>
        <span><span className="text-txt-label">Qty:</span> {child.quantity ?? '—'}</span>
      </div>
      {(child.error_code || child.error_message) && (
        <div className="rounded border border-bear/25 bg-bear/5 px-2 py-1.5 text-2xs font-mono text-bear break-words">
          {[child.error_code, child.error_message].filter(Boolean).join(' — ')}
        </div>
      )}
      <div className="text-2xs font-mono text-txt-muted break-all">
        Request: {child.child_client_request_id || '—'}<br />
        Signal request: {child.signal_request_id != null ? `#${child.signal_request_id}` : '—'} · Signal: {child.signal_id != null ? `#${child.signal_id}` : '—'}<br />
        Paper order: {child.paper_order_id || '—'} · Broker log: {child.broker_order_log_id != null ? `#${child.broker_order_log_id}` : '—'}
      </div>
    </div>
  );
}

function DetailContent({ detail, loading, error, onRetry }) {
  if (loading) {
    return <div className="flex justify-center items-center gap-2 py-14 text-txt-muted"><Spinner size={16} /><span className="text-xs font-mono">Loading fan-out details…</span></div>;
  }
  if (error || !detail?.event) {
    return (
      <div className="py-10 text-center space-y-3">
        <p className="text-xs font-mono text-bear">Unable to load fan-out details.</p>
        <button type="button" className="btn-ghost text-xs" onClick={onRetry}>Retry</button>
      </div>
    );
  }
  const children = detail.children || [];
  return (
    <div className="space-y-5">
      <section>
        <div className="flex items-center justify-between gap-3 mb-3">
          <h4 className="font-display font-semibold text-sm text-txt-primary">Parent Event</h4>
          <FanoutStatusBadge status={detail.event.status} />
        </div>
        <SummaryGrid event={detail.event} />
      </section>

      {detail.linked_entry_event && (
        <section className="rounded-md border border-info/25 bg-info/5 p-3">
          <h4 className="font-display font-semibold text-sm text-txt-primary mb-3">Linked Entry Event</h4>
          <SummaryGrid event={detail.linked_entry_event} />
        </section>
      )}

      <section>
        <h4 className="font-display font-semibold text-sm text-txt-primary mb-3">Child Executions ({children.length})</h4>
        {children.length === 0 ? (
          <p className="text-xs font-mono text-txt-muted py-5 text-center">No child execution rows.</p>
        ) : (
          <>
            <div className="lg:hidden space-y-2">
              {children.map((child) => <ChildCard key={child.child_execution_id} child={child} />)}
            </div>
            <div className="hidden lg:block overflow-x-auto rounded-md border border-bg-border">
              <table className="w-full text-left text-xs">
                <thead className="bg-bg-surface/70 text-2xs font-mono uppercase tracking-wider text-txt-label">
                  <tr>
                    <th className="px-3 py-2">Child</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Action</th>
                    <th className="px-3 py-2">Symbols</th>
                    <th className="px-3 py-2">Qty</th>
                    <th className="px-3 py-2">Error</th>
                    <th className="px-3 py-2">Identifiers</th>
                  </tr>
                </thead>
                <tbody>
                  {children.map((child) => (
                    <tr key={child.child_execution_id} className="border-t border-bg-border/60 align-top">
                      <td className="px-3 py-2 font-mono whitespace-nowrap">User #{child.child_user_id}<br /><span className="text-txt-muted">Deployment #{child.child_deployment_id}</span></td>
                      <td className="px-3 py-2"><FanoutStatusBadge status={child.status} /></td>
                      <td className="px-3 py-2 text-2xs font-mono whitespace-nowrap">{[child.event_action, child.signal_action].filter(Boolean).join(' · ') || '—'}</td>
                      <td className="px-3 py-2 min-w-64"><SymbolSummary source={child.symbol} resolved={child.resolved_symbol} compact /></td>
                      <td className="px-3 py-2 font-mono">{child.quantity ?? '—'}</td>
                      <td className="px-3 py-2 min-w-52 text-2xs font-mono text-bear break-words">{[child.error_code, child.error_message].filter(Boolean).join(' — ') || '—'}</td>
                      <td className="px-3 py-2 min-w-56 text-2xs font-mono text-txt-muted break-all">Request: {child.child_client_request_id || '—'}<br />Signal request: {child.signal_request_id ?? '—'} · Signal: {child.signal_id ?? '—'}<br />Paper: {child.paper_order_id || '—'} · Broker log: {child.broker_order_log_id ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <p className="text-2xs font-mono text-txt-muted border-t border-bg-border pt-3">
        Child Target/SL is not calculated independently; child exits follow master exit/Target/SL.
      </p>
    </div>
  );
}

function FanoutDetail({ eventId, onClose }) {
  const detailQ = useManualFanoutEventDetail(eventId, { enabled: !!eventId });
  if (!eventId) return null;
  const content = <DetailContent detail={detailQ.data} loading={detailQ.isLoading} error={detailQ.isError} onRetry={() => detailQ.refetch()} />;
  return (
    <>
      {typeof document !== 'undefined' && createPortal(
        <div className="hidden lg:flex fixed inset-0 z-[1100] items-center justify-center bg-black/60 backdrop-blur-sm p-6">
          <div className="card w-full max-w-5xl max-h-[90vh] flex flex-col shadow-panel">
            <div className="flex items-center justify-between px-5 py-4 border-b border-bg-border shrink-0">
              <h3 className="font-display font-semibold text-txt-primary">Manual Fan-out Event #{eventId}</h3>
              <button type="button" className="btn-ghost !p-2" onClick={onClose} aria-label="Close"><X size={16} /></button>
            </div>
            <div className="overflow-y-auto p-5">{content}</div>
          </div>
        </div>,
        document.body,
      )}
      <BottomSheet open onClose={onClose} title={`Manual Fan-out Event #${eventId}`}>
        {content}
      </BottomSheet>
    </>
  );
}

function EventMobileCard({ event, onView }) {
  return (
    <div className="rounded-md border border-bg-border bg-bg-surface/40 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-mono text-txt-primary">{event.event_action} · {fmtDateTime(event.created_at)}</p>
          <p className="text-2xs font-mono text-txt-muted">{String(event.fanout_scope || '—').replaceAll('_', ' ')}</p>
        </div>
        <FanoutStatusBadge status={event.status} />
      </div>
      <SymbolSummary source={event.symbol} base={event.base_symbol} resolved={event.resolved_symbol} compact />
      <div className="text-2xs font-mono text-txt-secondary">Children S/F/S/Q: {childCounts(event)}</div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-2xs font-mono text-txt-muted break-all">{masterReference(event)}</span>
        <button type="button" className="btn-ghost text-xs shrink-0" onClick={() => onView(event.event_id)}>View Details</button>
      </div>
    </div>
  );
}

export default function ManualFanoutPanel({ strategyId }) {
  const [open, setOpen] = useState(false);
  const [detailEventId, setDetailEventId] = useState(null);
  const params = useMemo(() => ({ master_strategy_id: strategyId, limit: 20, offset: 0 }), [strategyId]);
  const eventsQ = useManualFanoutEvents(params, { enabled: open && !!strategyId });
  const events = eventsQ.data?.items || [];

  return (
    <div className="border-t border-bg-border/70 pt-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex-1 flex items-center justify-between gap-3 py-2 text-left"
        >
          <span className="min-w-0">
            <span className="block text-sm font-display font-semibold text-txt-primary">Manual Fan-out</span>
            <span className="block text-2xs font-mono text-txt-muted">Read-only master and child execution status</span>
          </span>
          {open ? <ChevronDown size={16} className="shrink-0 text-txt-secondary" /> : <ChevronRight size={16} className="shrink-0 text-txt-secondary" />}
        </button>
        {open && (
          <button type="button" onClick={() => eventsQ.refetch()} disabled={eventsQ.isFetching} className="btn-ghost !p-2" aria-label="Refresh fan-out events">
            <RefreshCw size={13} className={eventsQ.isFetching ? 'animate-spin' : ''} />
          </button>
        )}
      </div>

      {open && (
        <div className="mt-3">
          {eventsQ.isLoading ? (
            <div className="flex justify-center items-center gap-2 py-10 text-txt-muted"><Spinner size={14} /><span className="text-xs font-mono">Loading manual fan-out events…</span></div>
          ) : eventsQ.isError ? (
            <div className="py-8 text-center space-y-3">
              <p className="text-xs font-mono text-bear">Unable to load manual fan-out events.</p>
              <button type="button" className="btn-ghost text-xs" onClick={() => eventsQ.refetch()}>Retry</button>
            </div>
          ) : events.length === 0 ? (
            <p className="text-xs font-mono text-txt-muted py-9 text-center">No manual fan-out events yet.</p>
          ) : (
            <>
              <div className="lg:hidden space-y-2">
                {events.map((event) => <EventMobileCard key={event.event_id} event={event} onView={setDetailEventId} />)}
              </div>
              <div className="hidden lg:block overflow-x-auto rounded-md border border-bg-border">
                <table className="w-full text-left text-xs">
                  <thead className="bg-bg-surface/70 text-2xs font-mono uppercase tracking-wider text-txt-label">
                    <tr>
                      <th className="px-3 py-2">Created</th>
                      <th className="px-3 py-2">Action</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Scope</th>
                      <th className="px-3 py-2">Symbols</th>
                      <th className="px-3 py-2">Children S/F/S/Q</th>
                      <th className="px-3 py-2">Master Reference</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event) => (
                      <tr key={event.event_id} className="border-t border-bg-border/60 align-top">
                        <td className="px-3 py-2 font-mono whitespace-nowrap">{fmtDateTime(event.created_at)}</td>
                        <td className="px-3 py-2 font-mono">{event.event_action}</td>
                        <td className="px-3 py-2"><FanoutStatusBadge status={event.status} /></td>
                        <td className="px-3 py-2 text-2xs font-mono whitespace-nowrap">{String(event.fanout_scope || '—').replaceAll('_', ' ')}</td>
                        <td className="px-3 py-2 min-w-64"><SymbolSummary source={event.symbol} base={event.base_symbol} resolved={event.resolved_symbol} compact /></td>
                        <td className="px-3 py-2 font-mono whitespace-nowrap">{childCounts(event)}</td>
                        <td className="px-3 py-2 min-w-44 text-2xs font-mono text-txt-muted break-all">{masterReference(event)}</td>
                        <td className="px-3 py-2"><button type="button" className="btn-ghost text-xs whitespace-nowrap" onClick={() => setDetailEventId(event.event_id)}>View Details</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {(eventsQ.data?.total || 0) > events.length && (
                <p className="text-2xs font-mono text-txt-muted mt-2">Showing latest {events.length} of {eventsQ.data.total} events.</p>
              )}
            </>
          )}
          <p className="text-2xs font-mono text-txt-muted mt-3">
            Child Target/SL is not calculated independently; child exits follow master exit/Target/SL.
          </p>
        </div>
      )}

      <FanoutDetail eventId={detailEventId} onClose={() => setDetailEventId(null)} />
    </div>
  );
}
